import { OrderedMap } from 'immutable';
import { timer } from 'rxjs';
import { filter, map } from 'rxjs/operators';

import { ActiveService } from '@entities/ActiveService';

export class OngoingTransactionalService {
  private readonly QueueName = 'sentiments.queue';
  private readonly ActiveTrackingIdName = 'sentiments.trackingId';
  private transactionalServices = OrderedMap<string, ActiveService>();
  private trackingId: string | undefined;

  constructor() {
    this.readAndParseRecords();
    this.cleanExpiredServices(5);
  }

  private readAndParseRecords() {
    const data = window.sessionStorage.getItem(this.QueueName);

    if (data && data !== '') {
      const parsedData = this.parse(data);
      const validatedData = this.validate(parsedData);
      // filter out isCompleted and status completed
      const notCompletedServices = validatedData.filter(x => !x.ic);
      this.transactionalServices = OrderedMap(notCompletedServices.map(x => [x.id, x]));
      // If there are corrupted values we are replacing them now.
      this.saveToSession(this.transactionalServices);
    }

    // read active tracking id
    this.trackingId = window.sessionStorage.getItem(this.ActiveTrackingIdName);
  }

  /**
   * Save transactional services to session storage [sync]
   * @private
   */
  private saveToSession(data: OrderedMap<string, ActiveService>): void {
    window.sessionStorage.setItem(this.QueueName, JSON.stringify(data.valueSeq().toArray()));
  }

  /**
   * JSON.parse session storage values safely
   * @param data
   * @private
   */
  private parse(data: string): ActiveService[] {
    try {
      return JSON.parse(data);
    } catch (err) {
      // remove corrupted values
      window.sessionStorage.removeItem(this.QueueName);
      return [];
    }
  }

  /**
   * Validate session storage values
   * @param data
   * @private
   */
  private validate(data: ActiveService[]): ActiveService[] {
    // Should be an array
    if (Array.isArray(data)) {
      return data
        .filter(x => {
          const { id, time, rs, ic } = x;

          if (!id && id !== '' && typeof id !== `string`) {
            return null;
          }

          if (!time && typeof time !== `number`) {
            return null;
          }

          if (!!rs && typeof rs !== `boolean`) {
            return null;
          }

          if (!!ic && typeof ic !== `boolean`) {
            return null;
          }

          return x;
        })
        .filter(x => x !== null);
    }

    // data is corrupt
    return [];
  }

  /**
   * Cleanup expired services
   * @param seconds
   * @param tsSessionTime = 20 minutes
   */
  private cleanExpiredServices(seconds: number, tsSessionTime = 1200000): void {
    timer(0, seconds * 1000)
      .pipe(
        filter(() => this.transactionalServices.size !== 0),
        map(() => new Date().getTime()),
      )
      .subscribe(currentTimeInMillisecond => {
        this.transactionalServices = this.transactionalServices.filterNot(x => {
          const timeDiff = currentTimeInMillisecond - x.time;
          return timeDiff >= tsSessionTime;
        });

        // sync set new list of transactional services
        this.saveToSession(this.transactionalServices);
      });
  }

  /**
   * Forcefully read values again from storage and remove completed records
   */
  forceRemoveValues() {
    this.readAndParseRecords();
  }

  /**
   * Get ongoing transactional service tracking ids
   */
  getOngoingServiceIds() {
    return this.transactionalServices.keySeq().toArray();
  }

  /**
   * Update status of the ongoing service's isRatingSubmitted property if the service received a rating
   * @param trackingId
   * @param status
   */
  updateIsRatingSubmitted(trackingId: string, status: boolean): ActiveService | null {
    const service = this.transactionalServices.get(trackingId);
    if (service) {
      service.rs = status;
      this.transactionalServices = this.transactionalServices.set(trackingId, service);
      this.saveToSession(this.transactionalServices);
      return service;
    }
    return null;
  }

  /**
   * Update is complete status of the ongoing service if this service is completed
   * @param trackingId
   * @param status
   */
  updateIsCompleted(trackingId: string, status: boolean): ActiveService | null {
    const service = this.transactionalServices.get(trackingId);
    if (service) {
      service.ic = status;
      this.transactionalServices = this.transactionalServices.set(trackingId, service);
      this.saveToSession(this.transactionalServices);
      return service;
    }
    return null;
  }

  isServiceStarted(trackingId: string): boolean {
    return this.transactionalServices.has(trackingId);
  }

  /**
   * Add new service
   * @param trackingId
   * @param time time in millis
   */
  addService({ id, time, tsAbQuestionTesting }: { id: string; time: number, tsAbQuestionTesting?: boolean }): ActiveService {
    const data: ActiveService = { id, time, tsAbQuestionTesting };
    this.transactionalServices = this.transactionalServices.set(id, data);
    this.saveToSession(this.transactionalServices);
    return data;
  }

  /**
   * Get given service
   * @param id
   */
  getService(id: string): ActiveService {
    return this.transactionalServices.get(id);
  }

  /**
   * Get number of services active
   */
  getNumberOfServices(): number {
    return this.transactionalServices.size;
  }

  /**
   * Reset the active tracking Id
   * This only should happen on page load
   */
  refreshActiveTrackingId(): void {
    const active = this.transactionalServices.filter(x => !x.ic);
    this.trackingId = active.reverse().first().id;
  }

  /**
   * Get ongoing service tracking id
   * It will be the latest started tracking id
   */
  getActiveTrackingId(): string | undefined {
    return this.trackingId;
  }

  /**
   * Set the active trackingId
   * @param trackingId
   */
  setActiveTrackingId(trackingId: string): void {
    this.trackingId = trackingId;
    window.sessionStorage.setItem(this.ActiveTrackingIdName, trackingId);
  }

  removeActiveTrackingId(): void {
    this.trackingId = undefined;
    window.sessionStorage.removeItem(this.ActiveTrackingIdName);
  }
}
